import React, { FC, useEffect, useRef } from 'react'
import { CellularPayPrompt } from './types'
import { AlertTriangleIcon } from './icons'
import { PromptDialog, PromptHeader } from './dialog-parts'
import { daysLabel, formatDate } from './format'
import { RECHECK_INTERVAL_MS } from './use-prompt-view'
import { MemberLockExits } from './member-lock-exits'
import { SimStatusNotice } from './sim-status-notice'
import { memberSimStageMessage, payableSimStage } from './sim-status'

const TITLE_ID = 'cellular-member-lock-title'
const DESCRIPTION_ID = 'cellular-member-lock-description'

const CONNECTION = 'The cellular connection'

// "Olive Owner (olive@example.com)", or whichever half the owner gave us.
const ownerLabel = ({ ownerName, ownerEmail }: CellularPayPrompt): string => {
  if (ownerName && ownerEmail) return `its owner ${ownerName} (${ownerEmail})`
  if (ownerName || ownerEmail) return `its owner ${ownerName || ownerEmail}`
  return 'the workspace owner'
}

const deadline = (prompt: CellularPayPrompt): string => {
  const due = formatDate(prompt.dueDate)
  if (prompt.daysPastDue > 0) {
    const was = due ? `, which was due ${due}` : ''
    return (
      `${was}. It is already ${daysLabel(prompt.daysPastDue)} past due, so ` +
      `${CONNECTION.toLowerCase()} can be switched off at any time until it is paid.`
    )
  }
  if (!due) return `. ${CONNECTION} is switched off once it is past due.`
  return `, due ${due}. ${CONNECTION} is switched off if it is not paid by then.`
}

// What someone working in the owner's workspace is told. As with the owner's own
// notice, the deadline is the API's due date, never a grace period written here.
export const memberLockNotice = (prompt: CellularPayPrompt): string =>
  `This workspace is locked until ${ownerLabel(prompt)} pays the cellular bill${deadline(prompt)}`

// "Contact the owner Olive Owner (olive@example.com) to pay it."
const contactOwner = ({ ownerName, ownerEmail }: CellularPayPrompt): string => {
  if (ownerName && ownerEmail) return `Contact the owner ${ownerName} (${ownerEmail}) to pay it.`
  if (ownerName || ownerEmail) return `Contact the owner ${ownerName || ownerEmail} to pay it.`
  return 'Contact the workspace owner to pay it.'
}

// Once the paywall reports a stage, it replaces the due-date wording: the member
// hears what has happened to the service and whom to ask, never "pay now".
export const memberStageNotice = (prompt: CellularPayPrompt): string =>
  `This workspace is locked until the cellular bill is paid. ${contactOwner(prompt)}`

// The owner may pay at any moment, and nothing on this dialog can be clicked to
// find out: ask again every few minutes and whenever the tab regains focus.
const useRecheck = (refresh: () => void): void => {
  const latest = useRef(refresh)
  useEffect(() => {
    latest.current = refresh
  })
  useEffect(() => {
    const recheck = () => latest.current()
    const timer = window.setInterval(recheck, RECHECK_INTERVAL_MS)
    window.addEventListener('focus', recheck)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', recheck)
    }
  }, [])
}

interface MemberLockDialogProps {
  prompt: CellularPayPrompt
  refresh: () => void
  // The workspace being viewed, so the way out lists only the others.
  workspaceId?: string
}

// The owner's back-bill, as a member of their workspace sees it: the same lock, but
// nothing to pay, give up or snooze, because only the owner can settle it. The one
// thing they can do is leave for one of their other workspaces.
export const MemberLockDialog: FC<MemberLockDialogProps> = ({ prompt, refresh, workspaceId }) => {
  useRecheck(refresh)
  return (
    <PromptDialog
      blocking
      describedBy={DESCRIPTION_ID}
      labelledBy={TITLE_ID}
      open
    >
      <PromptHeader
        descriptionId={DESCRIPTION_ID}
        icon={<AlertTriangleIcon size={28} />}
        id={TITLE_ID}
        title='This workspace is locked'
        tone='warning'
      >
        {payableSimStage(prompt) ? memberStageNotice(prompt) : memberLockNotice(prompt)}
      </PromptHeader>
      <SimStatusNotice
        message={memberSimStageMessage(prompt)}
        prompt={prompt}
      />
      <MemberLockExits lockedWorkspaceId={workspaceId} />
    </PromptDialog>
  )
}
